import {
  DEFAULT_BROWSE_LIMIT,
  DEFAULT_BROWSE_PAGE,
  DEFAULT_TAGS_MODE,
  MAX_SEARCH_QUERY_LENGTH,
} from "../constants";
import smoothActiveScrollToTop from "@utils/smoothActiveScrollToTop";

// Tags the backend uses for its own bookkeeping. They come back from the tags APIs
// looking like any other tag, but must never be offered to or shown to a reader.
// Compared lower-cased so a casing change upstream can't quietly un-hide one.
export const HIDDEN_TAG_NAMES = ["_system_recommendations"];

// Used by: useArticleTagsQuery (the tag dropdown) and useRecommendedTagsQuery.
export const isHiddenTag = (tagName) => {
  return HIDDEN_TAG_NAMES.includes(
    String(tagName ?? "")
      .trim()
      .toLowerCase(),
  );
};

// =========================================================================================
// Sort registry — one row per user-facing sort option.
// =========================================================================================
// `label` is both the dropdown text and the value held in component state, so a label is
// the only sort value any caller passes around. `sort`/`order` is the API pair, and the
// URL carries that same pair verbatim, so a Browse URL reads like the API call it makes:
//   /browse?sort=popular&order=desc  ->  /articles/q?sort=popular&order=desc
export const BROWSE_SORT_OPTIONS = [
  { label: "Most Recent", sort: "updated", order: "desc" },
  { label: "Oldest First", sort: "updated", order: "asc" },
  { label: "A-Z", sort: "title", order: "asc" },
  { label: "Z-A", sort: "title", order: "desc" },
  { label: "Most Popular", sort: "popular", order: "desc" },
];

// The API's own default ordering (sort=updated, order=desc), so it never has to appear in
// a URL or a query string.
export const DEFAULT_BROWSE_SORT = "Most Recent";

// The subset offered by the Browse tag-filter bar. Archive mode (DateSortFilter) offers
// its own alphabetical set from the same registry.
export const BROWSE_SORT_LABELS = [
  "Most Recent",
  "Oldest First",
  "Most Popular",
];

// The set offered by News Archive (DateSortFilter): the date and alphabetical orderings,
// without popularity. Derived from the registry rather than written out a second time —
// the hand-written copy that used to live in DateSortFilter kept offering "Oldest to
// Newest" after that label was renamed, so picking it resolved to no `{sort, order}` pair
// at all and the list quietly stayed on the default ordering.
export const ARCHIVE_SORT_LABELS = BROWSE_SORT_OPTIONS.filter((option) => {
  return option.sort !== "popular";
}).map((option) => {
  return option.label;
});

// Resolves a sort label to its `{ sort, order }` API pair.
// Returns null for the default ordering and for anything unrecognized, so every caller
// omits the params rather than writing a redundant or bogus pair.
// Used by: buildSearchParamsFromTags, buildArticleQueryParamsFromTags, sanitizeSearchParams.
export const getSortQuery = (sortLabel) => {
  const normalizedLabel = String(sortLabel ?? "")
    .trim()
    .toLowerCase();

  if (
    !normalizedLabel ||
    normalizedLabel === DEFAULT_BROWSE_SORT.toLowerCase()
  ) {
    return null;
  }

  const match = BROWSE_SORT_OPTIONS.find((option) => {
    return option.label.toLowerCase() === normalizedLabel;
  });

  return match ? { sort: match.sort, order: match.order } : null;
};

// Resolves a `sort`/`order` pair back to its label.
// An absent or unrecognized pair falls back to the default, so the dropdown always has a
// selection and a hand-edited URL can never strand it on nothing.
// Used by: useBrowseFilters, sanitizeSearchParams.
export const getSortFromSearchParams = (searchParams) => {
  const sort = searchParams.get("sort")?.trim().toLowerCase();
  const order = searchParams.get("order")?.trim().toLowerCase();

  const match = BROWSE_SORT_OPTIONS.find((option) => {
    return option.sort === sort && option.order === order;
  });

  return match ? match.label : DEFAULT_BROWSE_SORT;
};

// Normalizes a keyword search term for URL + API use (trimmed, length-capped).
// Used by: sanitizeSearchParams and useBrowseFilters.setQuery.
export const normalizeSearchQuery = (value) => {
  return String(value ?? "")
    .trim()
    .slice(0, MAX_SEARCH_QUERY_LENGTH)
    .trim();
};

// Builds a canonical URLSearchParams object for Browse filters.
// Needed to sanitize user-editable URL input and keep clean, shareable links.
// Used by: useBrowseFilters (read path + URL canonicalization effect).
//
// Param order here must match buildSearchParamsFromTags below, or the canonicalization
// effect would rewrite the URL forever comparing two equivalent-but-reordered strings.
export const sanitizeSearchParams = (
  inputSearchParams,
  { allowPageTags = true, allowQuery = true } = {},
) => {
  // URL-visible browse params are intentionally minimal, and named after the API params
  // they produce. We only keep optional non-default `page`, optional `tags`, optional
  // `pagetags`, optional `date`, a non-default `sort`/`order` pair, and optional `title`.
  const searchParams = new URLSearchParams(inputSearchParams);
  const nextParams = new URLSearchParams();

  const pageValue = Number(searchParams.get("page"));
  const safePage =
    Number.isFinite(pageValue) && pageValue > 0
      ? Math.floor(pageValue)
      : DEFAULT_BROWSE_PAGE;

  if (safePage !== DEFAULT_BROWSE_PAGE) {
    nextParams.set("page", String(safePage));
  }

  const tags = [
    ...new Set(
      (searchParams.get("tags") || "")
        .split(",")
        .map((value) => {
          return value.trim();
        })
        .filter(Boolean),
    ),
  ];

  if (tags.length > 0) {
    nextParams.set("tags", tags.join(","));
  }

  if (allowPageTags) {
    // `pagetags` is only honored for routes that do not already inject locked tags via props.
    const pageTags = [
      ...new Set(
        (searchParams.get("pagetags") || "")
          .split(",")
          .map((value) => {
            return value.trim();
          })
          .filter(Boolean),
      ),
    ];

    if (pageTags.length > 0) {
      nextParams.set("pagetags", pageTags.join(","));
    }
  }

  // Preserve date filter (YYYY-MM format)
  const dateParam = searchParams.get("date")?.trim();
  if (dateParam && /^\d{4}-\d{2}$/.test(dateParam)) {
    nextParams.set("date", dateParam);
  }

  // Round-trip the sort pair through the registry: an unrecognized pair resolves to the
  // default, and the default writes nothing. That drops junk and keeps the API's own
  // ordering out of the URL in one step.
  const sortQuery = getSortQuery(getSortFromSearchParams(searchParams));
  if (sortQuery) {
    nextParams.set("sort", sortQuery.sort);
    nextParams.set("order", sortQuery.order);
  }

  // Preserve the keyword only where the search UI is shown, so a hand-edited `title`
  // can't silently filter a view that has no search box to clear it.
  if (allowQuery) {
    const query = normalizeSearchQuery(searchParams.get("title"));
    if (query) {
      nextParams.set("title", query);
    }
  }

  return nextParams;
};

// Formats article created dates for Browse card display.
// Needed to keep date rendering consistent and resilient to backend date format differences.
// Used by: useBrowseArticlesQuery.
export const formatBrowseDate = (dateString) => {
  if (!dateString) {
    return "";
  }

  const normalizedDate = dateString.includes("T")
    ? dateString
    : `${dateString.replace(" ", "T")}Z`;
  const parsedDate = new Date(normalizedDate);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Resolves selected tag values from sanitized `tags` URL state.
// Needed so Browse can render chips/recommended tags directly from URL tag names.
// Used by: useBrowseFilters.
export const getTagsFromSearchParams = (searchParams) => {
  const tagNamesParam = searchParams.get("tags") || "";

  if (!tagNamesParam) {
    return [];
  }

  return tagNamesParam.split(",").map((value) => {
    return value.trim();
  });
};

// Resolves page-scoped tag values from sanitized `pagetags` URL state.
// Needed so Browse can be locked by route query when no page-level pageTags are supplied.
// Used by: useBrowseFilters.
export const getPageTagsFromSearchParams = (searchParams) => {
  const pageTagNamesParam = searchParams.get("pagetags") || "";

  if (!pageTagNamesParam) {
    return [];
  }

  return [
    ...new Set(
      pageTagNamesParam
        .split(",")
        .map((value) => {
          return value.trim();
        })
        .filter(Boolean),
    ),
  ];
};

// Every query param Browse reads or writes (see sanitizeSearchParams).
const BROWSE_URL_PARAMS = [
  "page",
  "tags",
  "pagetags",
  "date",
  "sort",
  "order",
  "title",
];

// Merges Browse's params into the current URL, keeping other components' params
// (e.g. the towing calculator's vehicle/year/trim). Used by useBrowseFilters when embedded.
export const mergeIntoForeignParams = (browseParams, currentParams) => {
  const nextParams = new URLSearchParams();
  currentParams.forEach((value, key) => {
    if (!BROWSE_URL_PARAMS.includes(key)) {
      nextParams.append(key, value);
    }
  });
  browseParams.forEach((value, key) => {
    nextParams.append(key, value);
  });
  return nextParams;
};

// Builds minimal URL params for client-side routing state.
// Needed so Browse links stay clean (`page=1` and the default sort hidden) while
// preserving selected tags, sort and keyword.
// Used by: useBrowseFilters.commitUrl.
export const buildSearchParamsFromTags = (
  tags,
  page = DEFAULT_BROWSE_PAGE,
  pageTags = [],
  date = "",
  sort = "",
  query = "",
) => {
  // Builds the minimal URL state used by useSearchParams.
  // Param order must match sanitizeSearchParams above (see the note there).
  const nextParams = new URLSearchParams();
  if (page !== DEFAULT_BROWSE_PAGE) {
    nextParams.set("page", String(page));
  }

  if (tags.length > 0) {
    nextParams.set("tags", tags.join(","));
  }

  if (pageTags.length > 0) {
    nextParams.set("pagetags", pageTags.join(","));
  }

  if (date) {
    nextParams.set("date", date);
  }

  const sortQuery = getSortQuery(sort);
  if (sortQuery) {
    nextParams.set("sort", sortQuery.sort);
    nextParams.set("order", sortQuery.order);
  }

  if (query) {
    nextParams.set("title", query);
  }

  return nextParams;
};

// Builds article-list API params from selected tags and page state.
// Needed to keep list fetch behavior consistent with Browse UI pagination/filtering.
// Used by: useBrowseFilters.articleListQueryParamsString.
export const buildArticleQueryParamsFromTags = (
  tags,
  page = DEFAULT_BROWSE_PAGE,
  date = "",
  sort = "",
  query = "",
  limit = DEFAULT_BROWSE_LIMIT,
) => {
  // Builds the articles list query used for card rendering.
  // Includes: optional `tags`, `limit` (12 unless the host sets rows), `page` only for page >= 2,
  // optional `date` (YYYY-MM), a non-default `sort`/`order` pair, and optional `title`.
  const nextParams = new URLSearchParams();
  nextParams.set("limit", String(limit));

  if (page !== DEFAULT_BROWSE_PAGE) {
    nextParams.set("page", String(page));
  }

  if (tags.length > 0) {
    nextParams.set("tags", tags.join(","));
  }

  if (tags.length > 1) {
    nextParams.set("tagsmode", DEFAULT_TAGS_MODE);
  }

  if (date) {
    nextParams.set("date", date);
  }

  // Every non-default option is sent through, popularity included — the API owns which
  // orderings it can serve. "Most Recent" is the API default, so it sends nothing.
  const sortQuery = getSortQuery(sort);
  if (sortQuery) {
    nextParams.set("sort", sortQuery.sort);
    nextParams.set("order", sortQuery.order);
  }

  // /articles/q `title` = case-insensitive substring match on article title
  if (query) {
    nextParams.set("title", query);
  }

  return nextParams;
};

// Builds count-only API params for pagination metadata.
// Needed to fetch total article count without full card payloads.
// Used by: useBrowseFilters.articleCountQueryParamsString.
export const buildArticleCountQueryParamsFromTags = (
  tags,
  date = "",
  query = "",
) => {
  // Builds pagination-only query state.
  // Includes: optional `tags`, `onlycount=true`, optional `date`, and optional `title`
  // (must mirror the list query so the total matches the cards). Ordering is irrelevant
  // to a count, so no `sort`/`order` here.
  const nextParams = new URLSearchParams();
  nextParams.set("onlycount", "true");

  if (tags.length > 0) {
    nextParams.set("tags", tags.join(","));
  }

  if (tags.length > 1) {
    nextParams.set("tagsmode", DEFAULT_TAGS_MODE);
  }

  if (date) {
    nextParams.set("date", date);
  }

  if (query) {
    nextParams.set("title", query);
  }

  return nextParams;
};

// Derives Browse's filter state from raw URL params, the single source of truth.
// Shared by useBrowseFilters and the /browse route loader so the API query-strings
// the loader prefetches are the exact ones the page then requests.
// `pageTags` (page-supplied locked tags) take precedence over URL `pagetags`.
export const getBrowseFilterState = (
  searchParams,
  { pageTags = [], enableSearch = true, dateFilter = "" } = {},
) => {
  const hasPageTagsFromProps = pageTags.length > 0;
  const sanitizedSearchParams = sanitizeSearchParams(searchParams, {
    allowPageTags: !hasPageTagsFromProps,
    allowQuery: enableSearch,
  });
  const page = Number(sanitizedSearchParams.get("page") || DEFAULT_BROWSE_PAGE);
  const urlTags = getTagsFromSearchParams(sanitizedSearchParams);
  const urlPageTags = getPageTagsFromSearchParams(sanitizedSearchParams);
  const query = sanitizedSearchParams.get("title") || "";
  const sortFilter = getSortFromSearchParams(sanitizedSearchParams);
  const lockedTags = hasPageTagsFromProps ? pageTags : urlPageTags;
  // Locked tags are prepended so the embedded page context stays visible in the UI
  // and is always included in the downstream API queries.
  const tags = [...new Set([...lockedTags, ...urlTags])];

  return {
    sanitizedSearchParams,
    page,
    urlTags,
    urlPageTags,
    query,
    sortFilter,
    lockedTags,
    tags,
    articleListQueryParamsString: buildArticleQueryParamsFromTags(
      tags,
      page,
      dateFilter,
      sortFilter,
      query,
    ).toString(),
    articleCountQueryParamsString: buildArticleCountQueryParamsFromTags(
      tags,
      dateFilter,
      query,
    ).toString(),
  };
};

// Query string for the tag options the filter bar offers for the selected tags.
export const buildArticleTagsQueryParamsString = (tags) =>
  tags.length > 0 ? `tags=${tags.join(",")}` : "";

// Computes pagination totals and navigation handlers for the Browse page.
// Needed to keep pagination behavior centralized and consistent with scroll-to-top UX.
// Used by: Browse page container.
export const getPaginationStuff = ({
  total,
  limit,
  page,
  setPage,
  scrollToTop = true,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / Math.max(limit, 1)));

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
      if (scrollToTop) smoothActiveScrollToTop();
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
      if (scrollToTop) smoothActiveScrollToTop();
    }
  };

  return {
    total,
    totalPages,
    handleNextPage,
    handlePrevPage,
  };
};

// Formats locked Browse page tags into concise tooltip text.
// Examples: ["Safety"] -> "Safety", ["Safety", "Hybrid"] -> "Safety & Hybrid".
// Used by: FilterManager tooltip copy.
export const getLockedTagsText = (lockedTags) => {
  if (lockedTags.length === 0) {
    return "";
  }

  if (lockedTags.length === 1) {
    return lockedTags[0];
  }

  if (lockedTags.length === 2) {
    return `${lockedTags[0]} & ${lockedTags[1]}`;
  }

  return `${lockedTags[0]}, ${lockedTags[1]} & ${lockedTags.length - 2} more`;
};
